import { startTransition, useOptimistic } from 'react';
import { useSearchParams } from 'react-router';
import { Check, PlusCircle } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';

export interface Option {
  label: string;
  value: string;
}

interface Props {
  options: Option[];
  queryKey: string;
  title: string;
}

export function DataTableFacetedFilter({ options, queryKey, title }: Props) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [optimisticValue, setOptimisticValue] = useOptimistic(
    searchParams.getAll(queryKey),
  );
  const selectedValues = new Set(optimisticValue);

  function onSelect(value: string) {
    const isolatedValue = value.split('__')[1];

    startTransition(() => {
      setSearchParams((prev) => {
        prev.set('page', '1');

        const previousValues = prev.getAll(queryKey);
        const included = previousValues.includes(isolatedValue);

        if (included) {
          const newValues = previousValues.filter((v) => v !== isolatedValue);

          setOptimisticValue(newValues);
          prev.delete(queryKey);
          newValues.forEach((v) => prev.append(queryKey, v));
        } else {
          setOptimisticValue([...previousValues, isolatedValue]);
          prev.append(queryKey, isolatedValue);
        }

        return prev;
      });
    });
  }

  function onClear() {
    startTransition(() => {
      setOptimisticValue([]);
      setSearchParams((prev) => {
        prev.set('page', '1');
        prev.delete(queryKey);

        return prev;
      });
    });
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button className="border-dashed text-xs" size="sm" variant="outline">
          <PlusCircle />
          {title}
          {selectedValues.size > 0 && (
            <>
              <Separator orientation="vertical" className="mx-2 h-4" />
              <Badge
                className="rounded-sm px-1 font-normal lg:hidden"
                variant="secondary"
              >
                {selectedValues.size.toLocaleString()}
              </Badge>
              <div className="hidden space-x-1 lg:flex">
                {selectedValues.size > 2 ? (
                  <Badge
                    className="rounded-sm px-1 font-normal"
                    variant="secondary"
                  >
                    {selectedValues.size.toLocaleString()} selected
                  </Badge>
                ) : (
                  options
                    .filter((o) => selectedValues.has(o.value))
                    .map((o) => (
                      <Badge
                        key={o.value}
                        className="rounded-sm px-1 font-normal"
                        variant="secondary"
                      >
                        {o.label}
                      </Badge>
                    ))
                )}
              </div>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-50 p-0">
        <Command>
          <CommandInput placeholder="Search" />
          <CommandList>
            <CommandEmpty>No results</CommandEmpty>
            <CommandGroup>
              {options.map(({ label, value }) => {
                const selected = selectedValues.has(value);

                return (
                  <CommandItem
                    key={value}
                    onSelect={onSelect}
                    value={`${label}__${value}`}
                  >
                    <div
                      className={cn(
                        'border-primary mr-2 flex size-4 items-center justify-center rounded-sm border',
                        selected
                          ? 'bg-primary text-primary-foreground'
                          : 'opacity-50 [&_svg]:invisible',
                      )}
                    >
                      <Check className="text-current" />
                    </div>
                    <span className="capitalize">{label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
            {selectedValues.size > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    className="justify-center text-center"
                    onSelect={onClear}
                  >
                    Clear filter
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
